import { morphism, createSchema } from 'morphism';
import './types.js';
import { fromError } from 'zod-validation-error';
import { N as NetError, m as geoEntry, n as itemURN, C as CategoryID, p as price, P as PackageSize, o as carrierID, q as servicePointRecap, r as destinationType, t as userAddress } from './zod-DfBqeaiX.js';
import { z } from 'zod';

const requestIDHeaderName = 'x-subito-req-id';
const REQ_ID_NOT_FOUND_MESSAGE = 'Request ID not found in request headers, generating a new one';
/*
 * Use with the Pages router (Express)
 */
const extractSbtHeaders = (req) => {
    const id = req.headers[requestIDHeaderName];
    let requestID;
    if (typeof id === 'string') {
        requestID = id;
    }
    else {
        requestID = crypto.randomUUID();
        console.warn(REQ_ID_NOT_FOUND_MESSAGE);
    }
    return {
        bottega: req.cookies?.bottega,
        requestID,
    };
};
/*
 * Use with the App router (Next.js)
 */
const extractSbtAppHeaders = (headers, cookies) => {
    const id = headers.get(requestIDHeaderName);
    let requestID;
    if (typeof id === 'string') {
        requestID = id;
    }
    else {
        requestID = crypto.randomUUID();
        console.warn(REQ_ID_NOT_FOUND_MESSAGE);
    }
    return {
        bottega: cookies.get('bottega')?.value,
        requestID,
    };
};

/**
 * Appends the given queryParams to the endpoint.
 * It skips null and undefined value and empty strings.
 */
const appendQueryParams = (endpoint, queryParams) => {
    if (!queryParams) {
        return endpoint;
    }
    const parsedUrl = new URL(endpoint);
    Object.entries(queryParams).forEach(([key, value]) => {
        const isNullOrUndefined = value === undefined || value === null;
        const isAnEmptyString = value === '';
        if (!isNullOrUndefined && !isAnEmptyString) {
            parsedUrl.searchParams.set(key, `${value}`);
        }
    });
    return parsedUrl.toString();
};

const fetchWithTimeout = async (url, requestInit, timeoutMs = 5000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(`Call to ${url} timed out after ${timeoutMs}ms`), timeoutMs);
    return fetch(url, {
        ...requestInit,
        signal: controller.signal,
    }).finally(() => clearTimeout(timeoutId));
};

const logAndThrow = (e, loggingOptions) => {
    if (loggingOptions) {
        const { logger, requestID } = loggingOptions;
        logger.logError(e.message, requestID);
    }
    else {
        console.error(e.message);
    }
    throw e;
};
const checkServerErrors = (noLogsForTheseErrorCodes = [], loggingOptions) => async (res) => {
    if (!res.ok) {
        try {
            const json = await res.json();
            const serverErrors = json?.errors;
            if (serverErrors)
                throw new NetError(`Error ${res.status} calling ${res.url}: ${serverErrors[0].info}`, {
                    status: res.status,
                    errors: serverErrors,
                    timeoutMs: json.timeout_ms,
                    backoffTimeMs: json.backoff_time_ms,
                });
            else
                throw Error('Unexpected error');
        }
        catch (e) {
            if (e instanceof NetError) {
                if (noLogsForTheseErrorCodes.includes(res.status))
                    throw e;
                else
                    logAndThrow(e, loggingOptions);
            }
            logAndThrow(new NetError(`Error ${res.status} calling ${res.url}: ${res.statusText}`, {
                status: res.status,
                errors: [],
            }), loggingOptions);
        }
    }
    return res;
};
const parseJSONWithErrors = async (res) => {
    try {
        return await res.json();
    }
    catch (e) {
        const err = e;
        logAndThrow(new NetError(`Error ${res.status} calling ${res.url}: ${err.message}`, {
            status: res.status,
            errors: err.message,
        }));
    }
};
const doNotMorph = (t) => t;
const morph = async (
// eslint-disable-next-line @typescript-eslint/no-explicit-any
json, schema) => {
    let morpher;
    if (schema === null)
        morpher = doNotMorph;
    else if (typeof schema === 'function')
        morpher = schema;
    else
        morpher = morphism(schema);
    return morpher(json);
};
const zodParse = async (res, morphedBody, zodSchema) => {
    if (!zodSchema)
        return morphedBody;
    const parsedBody = await zodSchema.safeParseAsync(morphedBody);
    if (!parsedBody.success) {
        const readableError = fromError(parsedBody.error);
        console.warn(`${readableError.toString()} calling ${res.url}`);
    }
    return parsedBody.success ? parsedBody.data : morphedBody;
};
const processBody = (morphismSchema, zodSchema) => async (res) => parseJSONWithErrors(res)
    .then((json) => morph(json, morphismSchema))
    .then((morphedBody) => zodParse(res, morphedBody, zodSchema))
    .then((parsedBody) => ({ status: res.status, body: parsedBody }));
const returnStatus = (res) => ({
    status: res.status,
});

const createHeadersFromSbtHeaders = (sbtHeaders) => {
    const headers = {};
    if (sbtHeaders?.bottega) {
        headers['cookie'] = `bottega=${sbtHeaders.bottega}`;
    }
    if (sbtHeaders?.authToken) {
        headers['Authorization'] = `Bearer ${sbtHeaders.authToken}`;
    }
    if (sbtHeaders?.environmentID) {
        headers['X-Subito-Environment-ID'] = sbtHeaders.environmentID;
    }
    if (sbtHeaders?.canarySupport) {
        headers['X-Subito-Canary-Support'] = sbtHeaders.canarySupport;
    }
    if (sbtHeaders?.forwardedFor) {
        headers['X-Forwarded-For'] = sbtHeaders.forwardedFor;
    }
    return headers;
};
const createHeadersForServerCall = (endpoint) => ({
    cookie: endpoint.sbtHeaders.bottega
        ? `bottega=${endpoint.sbtHeaders.bottega}`
        : '',
    'X-Subito-Channel': 'web',
    [requestIDHeaderName]: endpoint.sbtHeaders.requestID,
    ...createHeadersFromSbtHeaders(endpoint.sbtHeaders),
});
const fetchAndProcessErrors = (endpoint, requestInit, loggingOptions) => fetchWithTimeout(endpoint.url, requestInit, endpoint.timeoutMs).then(checkServerErrors(endpoint.acceptedErrorStatuses, loggingOptions));
const clientGet = async ({ queryParams, channelOverride, ...endpoint }) => fetchAndProcessErrors({ ...endpoint, url: appendQueryParams(endpoint.url, queryParams) }, {
    method: 'GET',
    headers: {
        'X-Subito-Channel': channelOverride ?? 'web',
        ...createHeadersFromSbtHeaders(endpoint.sbtHeaders),
    },
    credentials: 'include',
});
const clientPost = async ({ channelOverride, ...endpoint }, body) => fetchAndProcessErrors(endpoint, {
    method: 'POST',
    headers: {
        'Content-Type': 'application/json',
        'X-Subito-Channel': channelOverride ?? 'web',
        ...createHeadersFromSbtHeaders(endpoint.sbtHeaders),
    },
    credentials: 'include',
    body: JSON.stringify(body),
});
const clientPostForm = async ({ channelOverride, ...endpoint }, body) => fetchAndProcessErrors(endpoint, {
    method: 'POST',
    headers: {
        'X-Subito-Channel': channelOverride ?? 'web',
        ...createHeadersFromSbtHeaders(endpoint.sbtHeaders),
    },
    credentials: 'include',
    body,
});
const clientPut = async ({ channelOverride, ...endpoint }, body) => fetchAndProcessErrors(endpoint, {
    method: 'PUT',
    headers: {
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        'X-Subito-Channel': channelOverride ?? 'web',
        ...createHeadersFromSbtHeaders(endpoint.sbtHeaders),
    },
    credentials: 'include',
    body: JSON.stringify(body),
});
const clientPatch = async ({ channelOverride, ...endpoint }, body) => fetchAndProcessErrors(endpoint, {
    method: 'PATCH',
    headers: {
        'Content-Type': 'application/json',
        'X-Subito-Channel': channelOverride ?? 'web',
        ...createHeadersFromSbtHeaders(endpoint.sbtHeaders),
    },
    credentials: 'include',
    body: JSON.stringify(body),
});
const clientDelete = async ({ channelOverride, ...endpoint }, body) => fetchAndProcessErrors(endpoint, {
    method: 'DELETE',
    headers: {
        'Content-Type': 'application/json',
        'X-Subito-Channel': channelOverride ?? 'web',
        ...createHeadersFromSbtHeaders(endpoint.sbtHeaders),
    },
    credentials: 'include',
    body: JSON.stringify(body),
});
const serverGet = async ({ queryParams, logger, ...endpoint }) => fetchAndProcessErrors({ ...endpoint, url: appendQueryParams(endpoint.url, queryParams) }, { method: 'GET', headers: createHeadersForServerCall(endpoint) }, logger ? { logger, requestID: endpoint.sbtHeaders.requestID } : undefined);
const serverPut = async ({ logger, ...endpoint }, body) => fetchAndProcessErrors(endpoint, {
    method: 'PUT',
    headers: {
        'Content-Type': 'application/json',
        ...createHeadersForServerCall(endpoint),
    },
    body: JSON.stringify(body),
}, logger ? { logger, requestID: endpoint.sbtHeaders.requestID } : undefined);
const serverPost = async ({ logger, ...endpoint }, body) => fetchAndProcessErrors(endpoint, {
    method: 'POST',
    headers: {
        'Content-Type': 'application/json',
        ...createHeadersForServerCall(endpoint),
    },
    body: JSON.stringify(body),
}, logger ? { logger, requestID: endpoint.sbtHeaders.requestID } : undefined);

const voBannerColor = z.enum([
    'BLUE',
    'RED',
    'YELLOW',
    'PURPLE',
    'GREEN',
]);
const voBannerIcon = z.union([
    z.enum(['DISCOUNT', 'TRUCK']),
    z.string(),
]);
z.enum(['web', 'iphone', 'ipad', 'android']);
z.enum([
    'NEWAD',
    'EDITAD',
    'PROMOTE',
    'RESERVEDAREA',
]);
z.enum([
    'PROMOTE',
    'MANAGEADS',
    'LEGACY_ADIN_STEP_1',
]);
const voBanner = z.object({
    bannerId: z.number(),
    title: z.string(),
    description: z.string(),
    code: z.string().optional(),
    placeholders: z.array(z.string()).optional(),
    icon: voBannerIcon.nullable().optional(),
    color: voBannerColor.nullable().optional(),
});
const voBanners = z.object({
    banners: z.array(voBanner),
});

const createStrictSchema = (schema) => createSchema(schema, { undefinedValues: { strip: true } });
const createUnknownSchema = (schema) => createSchema(schema, { undefinedValues: { strip: true } });

const voBannerSchema = createStrictSchema({
    bannerId: 'banner_id',
    title: 'title',
    description: 'description',
    code: 'code',
    placeholders: 'placeholders',
    icon: 'icon',
    color: 'color',
});
const voBannersSchema = createStrictSchema({
    banners: {
        path: 'banners',
        fn: morphism(voBannerSchema),
    },
});

const promoDiscount = z.object({
    receiptItem: z.string(),
    type: z.enum(['fixed', 'percentage', 'amount']),
    value: z.number(),
});
const countdownConf = z.object({ alternativeSubtitle: z.string() });
const assetsPromo = z.object({
    resourceName: z.string().optional(),
    resourceUrl: z.string().optional(),
});
const promoContent = z.object({
    name: z.string(),
    title: z.string(),
    subtitle: z.string(),
    countdown: z.optional(countdownConf),
    assets: assetsPromo,
});
z.object({
    name: z.string(),
    giftProducts: z.array(z.string()),
    status: z.enum([
        'DRAFT',
        'BLOCKED',
        'SCHEDULED',
        'RUNNING',
        'CANCELLED',
        'DONE',
    ]),
    type: z.enum(['EXTRACTION', 'LIST', 'USERID']),
    userId: z.string().optional(),
    adIds: z.array(z.string()).optional(),
    startDate: z.date().optional(),
    endDate: z.date().optional(),
    purchasedProducts: z.array(z.string()).optional(),
});
const infoPromo = z.object({
    id: z.string(),
    channels: z.array(z.string()),
    startDate: z.string(),
    endDate: z.string(),
    criteria: z.array(z.record(z.string(), z.string())),
    discounts: z.array(promoDiscount),
    content: promoContent,
});
const promotions = z.object({
    promo: z.array(infoPromo),
});
const voucherValidatedOrderItem = z.object({
    productID: z.string(),
    amount: z.number(),
    subtotalAmount: z.number(),
});
const voucherValidatedOrder = z.object({
    amount: z.number(),
    totalAmount: z.number(),
    items: z.array(voucherValidatedOrderItem),
});

const PromoDiscountSchema = createStrictSchema({
    receiptItem: 'receipt_item',
    type: 'type',
    value: 'value',
});
const assetsPromoSchema = createStrictSchema({
    resourceName: 'resource_name',
    resourceUrl: 'resource_url',
});
const countdownConfSchema = createStrictSchema({
    alternativeSubtitle: 'alternative_subtitle',
});
const PromoContentSchema = createStrictSchema({
    name: 'name',
    title: 'title',
    subtitle: 'subtitle',
    countdown: {
        path: 'countdown',
        fn: morphism(countdownConfSchema),
    },
    assets: {
        path: 'assets',
        fn: morphism(assetsPromoSchema),
    },
});
const paidOptionsPromoSchema = createUnknownSchema({
    name: 'name',
    start_date: 'startDate',
    end_date: 'endDate',
    purchased_products: 'purchasedProducts',
    gift_products: 'giftProducts',
    status: 'status',
    type: 'type',
    ad_ids: 'adIds',
    user_id: {
        path: 'userId',
        fn: (stringUserId) => Number.parseInt(stringUserId, 10),
    },
});
const infoPromoSchema = createStrictSchema({
    id: 'id',
    channels: 'channels',
    startDate: 'start_date',
    endDate: 'end_date',
    criteria: 'criteria',
    discounts: {
        path: 'discounts',
        fn: morphism(PromoDiscountSchema),
    },
    content: {
        path: 'content',
        fn: morphism(PromoContentSchema),
    },
});
const promotionsSchema = createStrictSchema({
    promo: {
        path: 'promotions',
        fn: morphism(infoPromoSchema),
    },
});
const validationItemSchema = createStrictSchema({
    productID: 'product_id',
    amount: 'amount',
    subtotalAmount: 'subtotal_amount',
});
const voucherValidatedOrderSchema = createStrictSchema({
    amount: 'amount',
    totalAmount: 'total_amount',
    items: {
        path: 'items',
        fn: morphism(validationItemSchema),
    },
});

const regionSchema = createStrictSchema({
    uri: () => '/geo/region',
    id: 'key',
    value: 'value',
    friendlyName: 'friendly_name',
    label: 'label',
    level: 'level',
    neighbors: 'neighbors',
});
const regionReverseSchema = createUnknownSchema({
    uri: () => '/geo/region',
    id: 'key',
    value: 'value',
    friendly_name: 'friendlyName',
    label: 'label',
    level: 'level',
    neighbors: 'neighbors',
});
const regionsSchema = createStrictSchema({
    regions: {
        path: 'values',
        fn: morphism(regionSchema),
    },
});
const citySchema = createStrictSchema({
    uri: () => '/geo/city',
    id: 'key',
    value: 'value',
    label: 'label',
    level: 'level',
    friendlyName: 'friendly_name',
    istat: 'istat',
    regionId: 'region_id',
    shortName: 'short_name',
});
const cityReverseSchema = createUnknownSchema({
    uri: () => '/geo/city',
    id: 'key',
    value: 'value',
    label: 'label',
    level: 'level',
    friendly_name: 'friendlyName',
    istat: 'istat',
    region_id: 'regionId',
    short_name: 'shortName',
});
const townSchema = createStrictSchema({
    uri: () => '/geo/town',
    id: 'key',
    value: 'value',
    label: 'label',
    level: 'level',
    friendlyName: 'friendly_name',
    istat: 'istat',
    cityId: 'city_id',
    hasZone: 'has_zone',
    regionId: 'region_id',
});
const townReverseSchema = createUnknownSchema({
    uri: () => '/geo/town',
    id: 'key',
    value: 'value',
    label: 'label',
    level: 'level',
    friendly_name: 'friendlyName',
    istat: 'istat',
    city_id: 'cityId',
    has_zone: 'hasZone',
    region_id: 'regionId',
});
const zoneSchema = createStrictSchema({
    uri: () => '/geo/zone',
    id: 'key',
    value: 'value',
    label: 'label',
    level: 'level',
    cityId: 'city_id',
    regionId: 'region_id',
    townId: 'town_id',
});
createStrictSchema({
    zipCode: 'zip_code',
    town: 'town',
    province: 'province',
});
const mapSchema = createStrictSchema({
    address: 'address',
    latitude: 'latitude',
    longitude: 'longitude',
    zoom: 'zoom',
    showPin: {
        path: 'show_pin',
        // the non-strict equal is intentional
        fn: (showPin) => showPin == '1',
    },
});
const itemGeoSchema = createStrictSchema({
    uri: 'uri',
    type: 'type',
    label: 'label',
    region: {
        path: 'region',
        fn: morphism(regionSchema),
    },
    city: {
        path: 'city',
        fn: morphism(citySchema),
    },
    town: {
        path: 'town',
        fn: morphism(townSchema),
    },
    map: {
        path: 'map',
        fn: morphism(mapSchema),
    },
});
const zonesSchema = createStrictSchema({
    zones: {
        path: 'values',
        fn: morphism(zoneSchema),
    },
});
const geoEntrySchema = createStrictSchema({
    region: {
        path: 'region',
        fn: morphism(regionSchema),
    },
    city: {
        path: 'city',
        fn: morphism(citySchema),
    },
    town: {
        path: 'town',
        fn: morphism(townSchema),
    },
});
const geoEntryReverseSchema = createUnknownSchema({
    region: {
        path: 'region',
        fn: morphism(regionReverseSchema),
    },
    city: {
        path: 'city',
        fn: morphism(cityReverseSchema),
    },
    town: {
        path: 'town',
        fn: morphism(townReverseSchema),
    },
});
const geoEntriesSchema = createStrictSchema({
    entries: {
        path: 'data',
        fn: morphism(geoEntrySchema),
    },
});

const itemImageSchema = createStrictSchema({
    cdnBaseUrl: 'cdn_base_url',
});
const itemCategorySchema = createStrictSchema({
    id: 'key',
    label: 'value',
    parentId: 'macrocategory_id',
    friendlyName: 'friendly_name',
    weight: 'weight',
});
const itemAdvertiserSchema = createStrictSchema({
    name: 'name',
    type: 'type',
    phone: 'phone',
    userId: 'user_id',
    company: 'company',
    shopId: {
        path: 'shop_id',
        // from number to string
        fn: (shop_id) => typeof shop_id !== 'undefined' ? `${shop_id}` : shop_id,
    },
    shopName: 'shop_name',
    proTransactionsEnabled: 'pro_transactions_enabled',
});
const itemVisibilityOptionsSchema = createStrictSchema({
    gallery: 'gallery',
    urgent: 'urgent',
    smartbumpedAt: 'smartbumped_at',
});
const itemSchemaObj = {
    kind: () => 'AdItem',
    urn: 'urn',
    type: 'type',
    category: {
        path: 'category',
        fn: morphism(itemCategorySchema),
    },
    subject: 'subject',
    body: 'body',
    date: 'dates.display',
    images: {
        path: 'images',
        fn: morphism(itemImageSchema),
    },
    features: {
        path: 'features',
        fn: (features) => features.reduce((feats, rawFeature) => ({
            ...feats,
            [rawFeature.uri]: rawFeature,
        }), {}),
    },
    advertiser: {
        path: 'advertiser',
        fn: morphism(itemAdvertiserSchema),
    },
    geo: {
        path: 'geo',
        fn: morphism(itemGeoSchema),
    },
    visibilityOptions: {
        path: 'visibility_options',
        fn: morphism(itemVisibilityOptionsSchema),
    },
    urls: 'urls',
};
const itemSchema = createStrictSchema(itemSchemaObj);
const gallerizedItemSchema = createStrictSchema({
    ...itemSchemaObj,
    gallerized: () => true,
});
const singleAdSchema = (body) => {
    if (body instanceof Object &&
        'ads' in body &&
        Array.isArray(body.ads) &&
        body.ads.length === 1) {
        return morphism(itemSchema, body.ads[0]);
    }
    throw new Error('Result did not contain a single ad as expected');
};
const adOwnerVerificationSchema = createStrictSchema({
    userID: 'user_id',
    listID: 'list_id',
    isOwner: 'is_owner',
});
const correlatedTrackingInfo = createStrictSchema({
    transactionID: 'transaction-id',
    listName: 'list-name',
    itemID: 'item-id',
});
const correlatedAdsIdsSchema = createStrictSchema({
    ids: 'related-items',
    trackingInfo: {
        path: 'tracking-info',
        fn: morphism(correlatedTrackingInfo),
    },
});
const searchExtrasSchema = {
    advertiserTypeFacets: 'advertiser_facet.advertiser_type',
};
const userAdSchema = createStrictSchema({
    ...itemSchemaObj,
    editUrl: 'edit_url',
    expiresIn: 'expires_in',
    statistics: 'statistics',
    paidOptions: 'paid_options',
});
const userAdsSchema = createStrictSchema({
    total: 'count_all',
    checkNew: 'checknew',
    lines: 'lines',
    start: 'start',
    ads: {
        path: 'ads',
        fn: morphism(userAdSchema),
    },
    filterLabels: 'filters',
    extras: {
        path: 'extra',
        fn: morphism(searchExtrasSchema),
    },
});

const searchSchemaObj = createStrictSchema({
    total: 'count_all',
    checkNew: 'checknew',
    lines: 'lines',
    start: 'start',
    ads: {
        path: 'ads',
        fn: morphism(itemSchema),
    },
    filterLabels: 'filters',
});
const searchSchema = createStrictSchema(searchSchemaObj);
const gallerizedSearchSchema = createStrictSchema({
    ...searchSchemaObj,
    ads: {
        path: 'ads',
        fn: morphism(gallerizedItemSchema),
    },
});
const searchRecommendedSchema = createStrictSchema({
    count: 'count',
    filters: 'filters',
});
const filterValuesSchema = createStrictSchema({
    values: 'values',
    topValues: 'top_values',
});
const savedSearchSchema = createStrictSchema({
    filters: 'filters',
    searchId: 'search_id',
    insertionDate: 'insertion_date',
    updateDate: 'update_date',
    premiumExpirationDate: 'premium_expiration_date',
    sort: 'sort',
});
const savedSearchesSchema = createStrictSchema({
    savedSearches: {
        path: 'saved_searches',
        fn: morphism(savedSearchSchema),
    },
});
const listFilterSchemaObj = {
    uri: 'uri',
    type: 'type',
    label: 'label',
    dataSource: 'datasource',
    queryString: 'query_string',
    dependencies: 'dependencies',
    multiSelection: 'multiselection',
    searchable: 'searchable',
};
const listFilterSchema = createStrictSchema(listFilterSchemaObj);
const compositeFilterSchema = createStrictSchema({
    ...listFilterSchemaObj,
    subLabel: 'sublabel',
    min: {
        path: 'min',
        fn: morphism(listFilterSchema),
    },
    max: {
        path: 'max',
        fn: morphism(listFilterSchema),
    },
});
const filterConfigSchema = createStrictSchema({
    filters: {
        path: 'filters',
        fn: morphism(compositeFilterSchema),
    },
});

const userID = z.string().min(5).max(9);
const userStatus = z.enum(['ACTIVE', 'UNVERIFIED']);
const userGender = z.enum(['M', 'F']);
const user = z.object({
    userID,
    email: z.string().email(),
    status: userStatus,
    creationDate: z.string().datetime({ offset: true }),
});
const users = z.array(user);
const productSubscription = z.object({
    categories: z.array(z.object({
        key: z.string(),
        value: z.string(),
    })),
});
const userSubscription = z.object({
    subscriptionId: z.number(),
    statusSubscriptions: z.string(),
    startDate: z.string(),
    endDate: z.string(),
    product: productSubscription,
});
const userSubscriptions = z.object({
    subscriptions: z.array(userSubscription),
});
const loggedUser = z.object({
    userID,
    email: z.string().email(),
    username: z.string(),
});
const userProfileInfo = z.object({
    username: z.string(),
    showPhone: z.boolean(),
    isDefaultName: z.boolean().optional(),
    picture: z.string().optional(),
});
const userProfilePhone = z.object({
    show: z.boolean(),
    number: z.string().optional(),
    prefix: z.string().optional(),
});
const userProfile = z.object({
    username: z.string(),
    birthdate: z.string().optional(),
    geo: geoEntry.optional(),
    sex: userGender.optional(),
    phone: userProfilePhone.optional(),
});
const userContactInfo = z.object({
    adURN: itemURN,
    phoneNumber: z.string(),
    force: z.boolean(),
});
const userAdInfo = z.object({
    firstAdInsertionDate: z.string(),
    totalApprovedAds: z.number(),
    userCategoriesInfo: z.array(z.object({
        category: z.nativeEnum(CategoryID),
        approvedAds: z.number(),
    })),
});
const userEmail = z.object({
    email: z.string(),
    date: z.string(),
    channel: z.string(),
    adminId: z.string(),
});
const userEmails = z.object({ history: z.array(userEmail) });
const changeEmailSession = z.object({
    newEmail: z.string(),
    id: z.string(),
    challenges: z.array(z.object({
        type: z.enum(['account', 'new_email']),
        methods: z.array(z.enum(['email', 'phone'])),
    })),
});
const changeEmailProcess = z.object({
    account: z.object({
        email: z.string(),
        phone: z.optional(z.string()),
        hasSafeSession: z.optional(z.boolean()),
    }),
    session: z.optional(changeEmailSession),
});
const changeEmail2FAChallenge = z.object({
    token: z.string(),
});
const changeEmailIsEligible = z.object({
    isEligible: z.boolean(),
});

const userSchema = createStrictSchema({
    userID: 'user_id',
    email: 'email',
    status: 'status',
    creationDate: 'creation_date',
});
const usersSchema = (body) => Array.isArray(body) ? body.map(morphism(userSchema)) : [];
const subscriptionProductSchema = createStrictSchema({
    categories: 'product.categories',
});
const userSubscriptionSchema = createStrictSchema({
    subscriptionId: 'subscription_id',
    statusSubscriptions: 'status_subscriptions',
    startDate: 'start_date',
    endDate: 'end_date',
    product: morphism(subscriptionProductSchema),
});
const userSubscriptionsSchema = createStrictSchema({
    subscriptions: {
        path: 'subscriptions',
        fn: morphism(userSubscriptionSchema),
    },
});
const loggedUserSchema = createStrictSchema({
    userID: 'user_id',
    email: 'email',
    username: 'username',
});
const userProfileInfoSchema = createStrictSchema({
    username: 'name',
    showPhone: 'show_phone',
    isDefaultName: 'is_default_name',
    picture: 'picture',
});
const userProfilePhoneSchema = createStrictSchema({
    show: 'show',
    number: 'number',
    prefix: 'idd_prefix',
});
const userProfilePhoneReverseSchema = createUnknownSchema({
    show: 'show',
    number: 'number',
    idd_prefix: 'prefix',
});
const userProfileReverseSchema = createUnknownSchema({
    borndate: 'birthdate',
    name: 'username',
    gender: 'sex',
    geo: {
        path: 'geo',
        fn: morphism(geoEntryReverseSchema),
    },
    phone: {
        path: 'phone',
        fn: morphism(userProfilePhoneReverseSchema),
    },
});
const userProfileSchema = createStrictSchema({
    birthdate: 'borndate',
    username: 'name',
    sex: 'gender',
    geo: {
        path: 'geo',
        fn: morphism(geoEntrySchema),
    },
    phone: {
        path: 'phone',
        fn: morphism(userProfilePhoneSchema),
    },
});
const userContactInfoSchema = createStrictSchema({
    adURN: 'ad_urn',
    phoneNumber: 'phone_number',
    force: 'force',
});
const userAdInfoSchema = createStrictSchema({
    firstAdInsertionDate: 'first_ad_insertion_date',
    totalApprovedAds: 'total_num_approved_ads',
    userCategoriesInfo: {
        path: 'categories_num_approved_ads',
        fn: (ads) => {
            return ads.map((ad) => ({
                category: ad.category_id.toString(),
                approvedAds: ad.num_approved_ads,
            }));
        },
    },
});
const userEmailHistorySchema = createStrictSchema({
    email: 'email',
    date: 'date',
    channel: 'channel',
    adminId: 'admin_id',
});
const userEmailsHistorySchema = createStrictSchema({
    history: {
        path: 'history',
        fn: morphism(userEmailHistorySchema),
    },
});
const changeEmailSessionSchema = createStrictSchema({
    newEmail: 'new_email',
    id: 'id',
    challenges: {
        path: 'challenges',
        fn: (challenges) => {
            return challenges.map((challenge) => ({
                type: challenge.type,
                methods: challenge.methods,
            }));
        },
    },
});
const changeEmailProcessSchema = createStrictSchema({
    account: {
        email: 'account.email',
        phone: 'account.phone',
        hasSafeSession: 'account.is_safe',
    },
    session: {
        path: 'session',
        fn: (session) => {
            return session ? morphism(changeEmailSessionSchema, session) : undefined;
        },
    },
});
const changeEmail2FAChallengeSchema = createStrictSchema({
    token: 'token',
});
const changeEmailIsEligibleSchema = createStrictSchema({
    isEligible: 'is_eligible',
});

const pricePromoSchema = createStrictSchema({
    promotionName: 'promotion_name',
    promotionDiscount: {
        path: 'promotion_discount',
        fn: morphism(PromoDiscountSchema),
    },
});
const priceItemSchema = createStrictSchema({
    label: 'label',
    amount: 'amount',
    type: 'type',
    amountPrefixLabel: 'amount_prefix_label',
    strikethroughAmount: 'strikethrough_amount',
    promo: {
        path: 'promo',
        fn: morphism(pricePromoSchema),
    },
});
const pricesSchema = createStrictSchema({
    total: 'total',
    item: {
        path: 'item',
        fn: morphism(priceItemSchema),
    },
    extra: {
        path: 'extra',
        fn: morphism(priceItemSchema),
    },
});

const pricePromo = z.object({
    promotionName: z.string(),
    promotionDiscount: promoDiscount,
});
const priceType = z.enum([
    'ad',
    'light_shipping',
    'full_shipping',
    'fee',
]);
const priceItem = z.object({
    label: z.string(),
    amount: z.number(),
    type: priceType,
    amountPrefixLabel: z.string().optional(),
    strikethroughAmount: z.number().optional(),
    promo: z.optional(pricePromo),
});
const prices = z.object({
    total: z.number(),
    item: priceItem,
    extra: z.array(priceItem).nullable(),
});

const buttonStyle = z.enum(['solid', 'outline']);

const transactionID = z.string().uuid();
const transactionType = z.enum(['securepay', 'fullshipping']);
const userType = z.enum(['C2C', 'B2C']);
z.enum(['buyer', 'seller']);
const transactionState = z.enum([
    'NEW',
    'SALE_REQUESTED',
    'PURCHASE_REQUESTED',
    'PURCHASE_ACCEPTED',
    'CONFIRM_AVAILABILITY',
    'PAYMENT_CONFIRMED',
    'PAYMENT_CONFIRMATION_REMINDED',
    'ITEM_SHIPPED',
    'ITEM_RETURNING',
    'ITEM_RETURNED',
    'ITEM_AT_SERVICE_POINT',
    'ITEM_DELIVERED',
    'ITEM_RETURNABLE',
    'ITEM_RETURNABLE_AUTOMATICALLY',
    'EVERYONE_REFUNDED',
    'TO_BE_CLOSED_AUTOMATICALLY',
    'CLOSED',
    'CANCELED',
    'SELLER_CANCELED',
    'EXPIRED',
    'EXPIRED_REFUND_BUYER',
    'PURCHASE_REFUSED',
    'PAYMENT_CANCELED',
    'TRANSACTION_LOCKED',
    'REFUNDED',
    'FULL_REFUNDED',
    'GDPR_CLOSED',
    'GDPR_BUYER_DELETED',
    'GDPR_SELLER_DELETED',
    'PARTIAL_REFUNDED',
]);
const p2pstateStyle = z.enum(['success', 'warning', 'neutral']);
const p2pStateOption = z.enum([
    'is_final',
    'is_shipment_phase',
    'is_payment_phase',
    'display_original_price',
    'display_receipt',
    'display_shipping_address',
    'display_shipping_options',
    'display_buyer_assistant',
    'display_seller_assistant',
    'is_unavailable',
    'is_in_progress',
]);
const transactionP2PStateTip = z.object({
    securepayLabel: z.string().optional(),
    fullshippingLabel: z.string().optional(),
});
const transactionFlowURL = z.string().url();
const createdTransactionWithFlowURL = z.object({
    flowURL: transactionFlowURL,
});
const transactionAction = z.object({
    label: z.string(),
    flowURL: transactionFlowURL,
    style: buttonStyle,
});
const itemID = z.custom((val) => typeof val === 'string' ? /^ad(.{36}|\d{9})list\d{8,9}$/.test(val) : false);
const adInfoImage = z.object({
    cdnBaseUrl: z.string().url(),
});
const adInfo = z.object({
    urn: itemURN,
    title: z.string(),
    category: z.nativeEnum(CategoryID),
    price: z.optional(price),
    image: z.optional(adInfoImage),
});
const shippingInfo = z.object({
    origin: z.optional(userAddress),
    destination: z.nullable(userAddress),
    supportedDestinationTypes: z.optional(z.array(destinationType)),
    destinationType: z.optional(destinationType),
    destinationServicePoint: z.optional(servicePointRecap),
    shippingCarriers: z.optional(z.array(carrierID)),
    shippingOptionID: z.optional(z.nativeEnum(PackageSize)),
});
const transaction = z.object({
    transactionID,
    transactionType,
    buyerID: userID,
    buyerName: z.string(),
    sellerID: userID,
    sellerName: z.string(),
    state: transactionState,
    actions: z.array(transactionAction),
    itemID,
    itemPrice: z.optional(price),
    adInfo,
    sellerRevenue: z.optional(price),
    shippingAddressInfo: z.optional(shippingInfo),
    updatedAt: z.string().datetime({ offset: true }),
    userType,
    buyerPrice: z.optional(price),
});
const transactions = z.object({
    transactions: z.array(transaction),
});
const transactionRecap = z.object({
    transactionID,
    adTitle: z.string(),
    state: transactionState,
    transactionType,
    buyerID: userID,
    sellerID: userID,
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
const userTransaction = z.object({
    userID,
    email: z.string().email(),
    transactions: z.array(transactionRecap),
});
z.object({
    description: z.string(),
    phone_number: z.string().optional(),
    item_condition: z.string().optional(),
    item_description_matching: z.string().optional(),
    item_authenticity: z.string().optional(),
    tracking_number: z.string().optional(),
});
const returnReason = z.enum([
    'defective',
    'useless',
    'incorrect_item',
    'bought_by_mistake',
    'wrong_size',
]);
z.object({
    reason: z.string(returnReason).nullable(),
    description: z.string().nullable(),
    phone: z.string().nullable(),
});
const transactionP2PState = z.object({
    ID: z.string(),
    buyerLabel: z.string(),
    sellerLabel: z.string(),
    buyerHelpURL: z.string(),
    sellerHelpURL: z.string(),
    style: p2pstateStyle,
    options: z.array(p2pStateOption),
    buyerTip: transactionP2PStateTip.optional(),
    sellerTip: transactionP2PStateTip.optional(),
});
const transactionP2PStates = z.object({
    states: z.array(transactionP2PState),
});

const shippingRateSchema = createStrictSchema({
    optionID: 'option_id',
    carrierID: 'carrier_id',
    carrierName: 'carrier_name',
    carrierShortName: 'carrier_short_name',
    destinationType: 'destination_type',
    total: 'total',
    strikethroughTotal: 'strikethrough_total',
});
const shippingRatesSchema = createStrictSchema({
    rates: {
        path: 'rates',
        fn: morphism(shippingRateSchema),
    },
});
const servicePointRecapSchema = createStrictSchema({
    address: 'address',
    carrierID: 'carrier_id',
    carrierName: 'carrier_name',
    countryCode: 'country_code',
    name: 'name',
    postcode: 'postcode',
    servicePointID: 'service_point_id',
    town: 'town',
});

const userAddressSchema = createStrictSchema({
    address: 'address',
    addressLine2: 'address_line_2',
    id: 'id',
    name: 'name',
    number: 'number',
    postcode: 'postcode',
    province: 'province',
    state: 'state',
    surname: 'surname',
    town: 'town',
    phone: 'phone',
});
createStrictSchema({
    address: 'address',
    addressLine2: 'address_line_2',
    id: 'id',
    name: 'name',
    number: 'number',
    postcode: 'postcode',
    province: 'province',
    state: 'state',
    surname: 'surname',
    town: 'town',
    phone: 'phone',
    valid: 'valid',
});

const adInfoImageSchema = createStrictSchema({
    cdnBaseUrl: 'cdn_base_url',
});
const adInfoSchema = createStrictSchema({
    urn: 'urn',
    title: 'title',
    category: 'category',
    price: 'price',
    image: {
        path: 'image',
        fn: morphism(adInfoImageSchema),
    },
});
const shippingAddressInfoSchema = createStrictSchema({
    destination: {
        path: 'destination',
        fn: morphism(userAddressSchema),
    },
    destinationServicePoint: {
        path: 'destination_service_point',
        fn: morphism(servicePointRecapSchema),
    },
    destinationType: 'destination_type',
    supportedDestinationTypes: 'supported_destination_types',
    shippingOptionID: 'shipping_option_id',
    shippingCarriers: 'shipping_carriers',
    origin: {
        path: 'origin',
        fn: morphism(userAddressSchema),
    },
});
const shippingStateActionSchema = createStrictSchema({
    label: 'label',
    flowURL: 'flow_url',
    style: 'style',
});
const transactionSchema = createStrictSchema({
    transactionID: 'transaction_id',
    transactionType: 'transaction_type',
    buyerID: 'buyer_id',
    buyerName: 'buyer_name',
    sellerID: 'seller_id',
    sellerName: 'seller_name',
    state: 'state',
    actions: {
        path: 'actions',
        fn: morphism(shippingStateActionSchema),
    },
    itemID: 'item_id',
    itemPrice: 'item_price',
    adInfo: {
        path: 'ad_info',
        fn: morphism(adInfoSchema),
    },
    sellerRevenue: 'seller_revenue',
    shippingAddressInfo: {
        path: 'shipping_address_info',
        fn: morphism(shippingAddressInfoSchema),
    },
    updatedAt: 'updated_at',
    userType: 'user_type',
    buyerPrice: 'buyer_price',
});
const transactionsSchema = createStrictSchema({
    transactions: {
        path: 'transactions',
        fn: morphism(transactionSchema),
    },
});
const transactionRecapSchema = createStrictSchema({
    transactionID: 'transaction_id',
    adTitle: 'ad_title',
    state: 'state',
    transactionType: 'transaction_type',
    buyerID: 'buyer_id',
    sellerID: 'seller_id',
    createdAt: 'created_at',
    updatedAt: 'updated_at',
});
const userTransactionSchema = createStrictSchema({
    userID: 'user_id',
    email: 'email',
    transactions: {
        path: 'transactions',
        fn: morphism(transactionRecapSchema),
    },
});
const transactionP2PStateTipSchema = createStrictSchema({
    securepayLabel: 'securepay_label',
    fullshippingLabel: 'fullshipping_label',
});
const transactionP2PStateSchema = createStrictSchema({
    ID: 'id',
    buyerLabel: 'buyer_label',
    sellerLabel: 'seller_label',
    buyerHelpURL: 'buyer_help_url',
    sellerHelpURL: 'seller_help_url',
    style: 'style',
    options: 'options',
    buyerTip: {
        path: 'buyer_tip',
        fn: morphism(transactionP2PStateTipSchema),
    },
    sellerTip: {
        path: 'seller_tip',
        fn: morphism(transactionP2PStateTipSchema),
    },
});
const transactionP2PStatesSchema = createStrictSchema({
    states: {
        path: 'states',
        fn: morphism(transactionP2PStateSchema),
    },
});
const createdTransactionWithFlowUrlSchema = createStrictSchema({
    flowURL: 'flow_url',
});

const conversationIDSchema = (body) => body instanceof Object &&
    '_embedded' in body &&
    body._embedded instanceof Object &&
    'conversations' in body._embedded &&
    Array.isArray(body._embedded.conversations) &&
    body._embedded.conversations.length > 0 &&
    typeof body._embedded.conversations[0].conversationId === 'string'
    ? body._embedded.conversations[0].conversationId
    : null;
const uploadMessagingAttachmentSchema = createStrictSchema({
    url: 'url',
    contentType: 'content_type',
    path: 'path',
});
const unreadMessagesSchema = createStrictSchema({
    pollingTime: 'polling_time',
    unread: 'unread',
});
const channelIDBodySchema = createStrictSchema({
    id: 'id',
    cid: 'cid',
});

const conversationID = z.string().nullable();
const authToken = z.object({
    token: z.string(),
});
const messagingAttachment = z.object({
    path: z.string().optional(),
    contentType: z.string().optional(),
    url: z.string().optional(),
});
z.object({
    text: z.string(),
    attachments: z.array(messagingAttachment).optional(),
    clientMessageId: z.string().optional(),
});
const messagingMessageResponse = z.object({
    id: z.string(),
});
const presentationStyleWeb = z.enum(['modal', 'newtab']);
const messagingTriggerAction = z.object({
    flow_url: z.string(),
    label: z.string(),
    style: z.enum(['solid', 'outline']),
    // e.g modal is default, newtab is used for external links
    presentation_style_web: presentationStyleWeb.optional(),
});
const messagingTriggerActionsResponse = z.object({
    actions: z.array(messagingTriggerAction),
});
const unreadMessages = z.object({
    unread: z.number(),
    pollingTime: z.number(),
});
const channelIDBody = z.object({
    id: z.string(),
    cid: z.string(),
});

const shopCategorySchema = createStrictSchema({
    key: 'key',
    value: 'value',
    friendlyName: 'friendly_name',
    macrocategoryId: 'macrocategory_id',
    weight: 'weight',
});
const shopSchema = createStrictSchema({
    shopId: 'shop_id',
    name: 'name',
    logo: 'logo',
    adsCount: 'ads_count',
    address: 'address',
    urls: 'urls',
    website: 'website',
    openingTime: 'opening_time',
    social: 'social',
    shopCategories: {
        path: 'shop_categories',
        fn: morphism(shopCategorySchema),
    },
    activeShopCategories: {
        path: 'active_shop_categories',
        fn: morphism(shopCategorySchema),
    },
});
const shopsSchema = createStrictSchema({
    countAll: 'count_all',
    lines: 'lines',
    start: 'start',
    shops: {
        path: 'shops',
        fn: morphism(shopSchema),
    },
});
const shopGoogleReviewSchema = createStrictSchema({
    rating: 'rating',
    comment: 'comment',
    publishedAt: 'published_at',
    author: 'author',
});
const shopGoogleReviewsLinks$1 = createStrictSchema({
    placeUri: 'place_uri',
});
const shopGoogleReviewsSchema = createStrictSchema({
    shopId: 'shop_id',
    rating: 'rating',
    reviewCount: 'review_count',
    reviews: {
        path: 'reviews',
        fn: morphism(shopGoogleReviewSchema),
    },
    links: {
        path: 'links',
        fn: morphism(shopGoogleReviewsLinks$1),
    },
});

const shopLogoImage = z.object({
    uri: z.string(),
    secureuri: z.string(),
});
const shopLogo = z.object({
    uri: z.string(),
    big: shopLogoImage.optional(),
    medium: shopLogoImage.optional(),
    small: shopLogoImage.optional(),
});
const shopUrls = z.object({
    default: z.string(),
    mobile: z.string(),
});
const shopDayHours = z.object({
    status: z.string(),
    from: z.string().optional(),
    to: z.string().optional(),
});
const shopOpeningTimeDay = z.object({
    day: shopDayHours,
});
const shopOpeningTimePeriod = z.object({
    am: shopDayHours,
    pm: shopDayHours,
});
const shopOpeningDayName = z.union([
    z.literal('mon'),
    z.literal('tue'),
    z.literal('wed'),
    z.literal('thu'),
    z.literal('fri'),
    z.literal('sat'),
    z.literal('sun'),
]);
const shopOpeningTime = z.record(shopOpeningDayName, z.union([shopOpeningTimeDay, shopOpeningTimePeriod]));
const shopSocialLinks = z.object({
    facebook: z.string().optional(),
    instagram: z.string().optional(),
    linkedin: z.string().optional(),
});
const shopCategory = z.object({
    key: z.string(),
    value: z.string(),
    friendlyName: z.string(),
    macrocategoryId: z.string(),
    weight: z.number(),
});
const shop = z.object({
    shopId: z.number().optional(),
    name: z.string(),
    logo: shopLogo.optional(),
    adsCount: z.number().optional(),
    address: z.string().optional(),
    urls: shopUrls.optional(),
    website: z.string().optional(),
    openingTime: shopOpeningTime.optional(),
    social: shopSocialLinks.optional(),
    shopCategories: z.array(shopCategory).optional(),
    activeShopCategories: z.array(shopCategory).optional(),
});
const shops = z.object({
    countAll: z.number(),
    lines: z.number(),
    start: z.number(),
    shops: z.array(shop),
});
const shopGoogleReviewsEntry = z.object({
    rating: z.number().optional(),
    comment: z.string().optional(),
    publishedAt: z.string().optional(),
    author: z.string().optional(),
});
const shopGoogleReviewsEntries = z.array(shopGoogleReviewsEntry);
const shopGoogleReviewsLinks = z.object({
    placeUri: z.string().optional(),
});
const shopGoogleReviews = z.object({
    shopId: z.string(),
    rating: z.number().optional(),
    reviewCount: z.number().optional(),
    reviews: shopGoogleReviewsEntries.optional(),
    links: shopGoogleReviewsLinks.optional(),
});

// Requests
const payoutPrivateInfoPUTSchema = createUnknownSchema({
    date_of_birth: {
        path: 'dateOfBirth',
        fn: (date) => {
            const [day, month, year] = date.split('/');
            return `${year}-${month}-${day}`;
        },
    },
    first_name: 'firstName',
    last_name: 'lastName',
});
const payoutCompanyInfoPUTSchema = createUnknownSchema({
    company_name: 'companyName',
});
const payoutUserSchema = createUnknownSchema({
    hyperwallet_id: 'userToken',
    address: 'address',
    city: 'city',
    province: 'province',
    country: 'country',
    postal_code: 'postalCode',
    is_company: 'isCompany',
    private_info: {
        path: 'privateInfo',
        fn: morphism(payoutPrivateInfoPUTSchema),
    },
    company_info: {
        path: 'companyInfo',
        fn: morphism(payoutCompanyInfoPUTSchema),
    },
});
// Responses
const walletTypeSchema = createStrictSchema({
    walletType: 'wallet_type',
});
const payoutPrivateInfoSchema = createStrictSchema({
    dateOfBirth: {
        path: 'date_of_birth',
        fn: (HWdate) => {
            const [year, month, day] = HWdate.split('-');
            return `${day}/${month}/${year}`;
        },
    },
    firstName: 'first_name',
    lastName: 'last_name',
});
const payoutCompanyInfoSchema = createStrictSchema({
    companyName: 'company_name',
});
const fullPayoutUserSchema = createStrictSchema({
    userToken: 'hyperwallet_id',
    address: 'address',
    city: 'city',
    province: 'province',
    country: 'country',
    postalCode: 'postal_code',
    isCompany: 'is_company',
    privateInfo: {
        path: 'private_info',
        fn: morphism(payoutPrivateInfoSchema),
    },
    companyInfo: {
        path: 'company_info',
        fn: morphism(payoutCompanyInfoSchema),
    },
    selectedTransferMethod: 'selected_transfer_method.hyperwallet_token',
    canReceivePayments: 'can_receive_payments',
    verificationInProgress: 'verification_in_progress',
});
const payoutMethodSchema = createStrictSchema({
    type: 'type',
    token: 'token',
});
const payoutMethodsSchema = (body) => body instanceof Object && 'data' in body && Array.isArray(body.data)
    ? body.data.map(morphism(payoutMethodSchema))
    : [];
const tokenSchema = (body) => body instanceof Object && 'value' in body && typeof body.value === 'string'
    ? body.value
    : '';
const paymentSchema = createStrictSchema({
    status: 'status',
    paymentID: 'payment_id',
});

z.enum(['PRIVATE', 'BUSINESS']);
const wallet = z.object({
    walletType: z.enum(['PRIVATE', 'BUSINESS']),
});
const hyperwalletAuthToken = z.string();
const hyperwalletMethodToken = z.string();
const hyperwalletUserToken = z.string();
const payoutPrivateInfo = z.object({
    firstName: z.string(),
    lastName: z.string(),
    dateOfBirth: z.string(),
});
const payoutCompanyInfo = z.object({
    companyName: z.string(),
});
const payoutUser = z.object({
    userToken: z.optional(hyperwalletUserToken),
    address: z.string(),
    city: z.string(),
    province: z.string(),
    country: z.string(),
    postalCode: z.string(),
    isCompany: z.boolean(),
    privateInfo: z.optional(payoutPrivateInfo),
    companyInfo: z.optional(payoutCompanyInfo),
});
const fullPayoutUser = payoutUser.extend({
    userToken: hyperwalletUserToken,
    selectedTransferMethod: z.nullable(hyperwalletMethodToken),
    canReceivePayments: z.boolean(),
    verificationInProgress: z.boolean(),
});
const payoutMethodType = z.enum(['BANK_ACCOUNT', 'PAYPAL_ACCOUNT']);
const payoutMethod = z.object({
    token: hyperwalletMethodToken,
    type: payoutMethodType,
});
const payoutMethods = z.array(payoutMethod);
const paymentStatus = z.enum([
    'SETTLED',
    'PAYOUT_CANCELED',
    'PAYOUT_FAILED',
    'REFUNDED',
    'PAYOUT_IN_PROGRESS',
    'VOIDED',
    'COMPLETED',
    'PREAUTHORIZED',
]);
const payment = z.object({
    status: paymentStatus,
    paymentID: z.string().optional(),
});

const voOptionSchema = createStrictSchema({
    name: 'name',
    price: 'price',
    originalPrice: 'price_original',
    label: 'label',
    groupName: 'gr_name',
    state: 'state',
    bundleList: 'bundle_list',
});
const voGroupSchema = createStrictSchema({
    name: 'name',
    description: 'description',
    label: 'label',
    excludedGroups: 'exclude_groups',
    options: {
        path: 'paid_options',
        fn: morphism(voOptionSchema),
    },
});
const voGroupsSchema = createStrictSchema({
    groups: {
        path: 'groups',
        fn: morphism(voGroupSchema),
    },
});
const pfOptionSchema = createStrictSchema({
    name: 'name',
    price: 'price',
    originalPrice: 'price_original',
    label: 'label',
    groupName: 'gr_name',
    state: 'state',
    duration: 'duration',
});
const pfGroupSchema = createStrictSchema({
    name: 'name',
    description: 'description',
    label: 'label',
    options: {
        path: 'premium_features',
        fn: morphism(pfOptionSchema),
    },
});
const pfGroupsSchema = createStrictSchema({
    groups: {
        path: 'groups',
        fn: morphism(pfGroupSchema),
    },
});
const poFeeSchema = createStrictSchema({
    originalPrice: 'price_original',
    price: 'price',
    name: 'name',
    description: 'description',
    label: 'label',
    title: 'title',
    state: 'state',
});
const feesSchema = (body) => ({
    fees: Array.isArray(body)
        ? body.map((data) => morphism(poFeeSchema, data))
        : [],
});
const purchasedPoOrderSchema = createStrictSchema({
    transactionId: 'transaction_id',
});
const plutusPurchaseTokenSchema = createStrictSchema({
    token: 'value',
});

const publicationFeeNames = ['ad_pay', 'ad_pay_2'];
const extraFeeNames = ['more_photos'];
z.enum(publicationFeeNames);
z.enum(extraFeeNames);
const poFeeName = z.enum([...publicationFeeNames, ...extraFeeNames]);
const voGroupName = z.enum([
    'bounce',
    'gallery',
    'bundle',
    'urgent',
    'homepage_recommender',
    'detail_recommender',
]);
z.enum([
    'NEWAD',
    'EDITAD',
    'PROMOTE',
    'NOTIFICATION',
    'ADEDITREFUSE',
    'ADPROLONG_V2',
    'ADPROLONG_V2_EXPIRING',
    'HOMEPAGE',
    'RESERVEDAREA_PURCHASE',
    'RESERVEDAREA_PROMOTE',
    'ADDETAIL_PROMOTE',
]);
const optionStatus = z.enum(['available', 'unavailable']);
const voOptionName = z.string();
const voOption = z.object({
    name: voOptionName,
    price: z.number(),
    originalPrice: z.number(),
    label: z.string(),
    groupName: voGroupName,
    state: optionStatus,
    bundleList: z.array(voOptionName),
});
const voGroup = z.object({
    name: voGroupName,
    description: z.string(),
    label: z.string(),
    excludedGroups: z.array(voGroupName),
    options: z.array(voOption),
});
const voGroups = z.object({ groups: z.array(voGroup) });
const pfGroupName = z.enum(['saved_search']);
const pfEntrypoint = z.enum(['SAVEDSEARCH']);
const pfOption = z.object({
    name: z.string(),
    price: z.number(),
    originalPrice: z.number(),
    label: z.string(),
    groupName: pfGroupName,
    state: optionStatus,
    duration: z.number(),
});
const pfGroup = z.object({
    name: pfGroupName,
    description: z.string(),
    label: z.string(),
    options: z.array(pfOption),
});
const pfGroups = z.object({ groups: z.array(pfGroup) });
const baseFee = z.object({
    name: poFeeName,
    price: z.number(),
    description: z.string(),
    title: z.string(),
    label: z.string(),
});
const publicationFee = baseFee;
const poFee = baseFee.extend({
    originalPrice: z.number(),
    state: optionStatus,
});
const poFees = z.object({ fees: z.array(poFee) });
const poCartRedirect = z.object({
    cartCookieKey: z.string().optional(),
    redirect: z.string(),
});
const poOrderItem = z.object({
    ad_id: z.string(),
    paid_options: z.array(z.object({
        name: z.union([voOptionName, poFeeName]),
        label: z.string(),
        price: z.number(),
        originalPrice: z.number().optional(),
    })),
});
// The type below will become a z.union when we'll support more premium feature
const pfOrderItem = 
// saved search premium feature
z.object({
    paid_option: z.object({
        name: z.literal('saved_search'),
        label: z.string(),
        price: z.number(),
        originalPrice: z.number().optional(),
    }),
    payload: z.object({
        category: z.number(),
        saved_search_id: z.string(),
    }),
});
const pfOrder = z.object({
    entry_point: pfEntrypoint,
    items: z.array(pfOrderItem),
    afterPaymentReturnTo: z.string().optional(),
});
z.union([
    // with ad version
    z.object({
        entry_point: z.enum(['NEWAD', 'EDITAD', 'ADEDITREFUSE']),
        items: z.array(poOrderItem.extend({ action_id: z.string() })),
        afterPaymentReturnTo: z.string().optional(),
    }),
    // without ad version
    z.object({
        entry_point: z.enum([
            'PROMOTE',
            'HOMEPAGE',
            'NOTIFICATION',
            'RESERVEDAREA_PROMOTE',
            'RESERVEDAREA_PURCHASE',
            'ADDETAIL_PROMOTE',
            'ADPROLONG_V2',
            'ADPROLONG_V2_EXPIRING',
        ]),
        items: z.array(poOrderItem),
        afterPaymentReturnTo: z.string().optional(),
    }),
    // for premium features only
    pfOrder,
]);
const confirmedPoOrderItem = z.union([
    // visibility options and fees
    z.object({
        ad_id: z.string(),
        paid_options: z.array(z.union([voOptionName, poFeeName])),
        action_id: z.string().optional(),
    }),
    // premium features
    z.object({
        paid_option: z.literal('saved_search'),
        payload: z.object({
            category: z.number(),
            saved_search_id: z.string(),
        }),
    }),
]);
z.object({
    nonce: z.string(),
    amount: z.number(),
    items: z.array(confirmedPoOrderItem),
    voucher: z.string().optional(),
});
const purchasedPoOrder = z.object({
    transactionId: z.string(),
});
const plutusPurchaseToken = z.object({ token: z.string() });

export { changeEmailProcess as $, userAdInfo as A, userAdInfoSchema as B, clientPostForm as C, conversationID as D, conversationIDSchema as E, authToken as F, messagingMessageResponse as G, messagingAttachment as H, uploadMessagingAttachmentSchema as I, messagingTriggerActionsResponse as J, unreadMessages as K, unreadMessagesSchema as L, channelIDBody as M, channelIDBodySchema as N, shops as O, shopsSchema as P, fetchAndProcessErrors as Q, payoutMethods as R, payoutMethodsSchema as S, voGroups as T, voGroupsSchema as U, publicationFee as V, pfGroups as W, pfGroupsSchema as X, clientDelete as Y, savedSearchSchema as Z, savedSearchesSchema as _, clientPost as a, changeEmailProcessSchema as a0, changeEmail2FAChallenge as a1, changeEmail2FAChallengeSchema as a2, createStrictSchema as a3, payoutUserSchema as a4, correlatedAdsIdsSchema as a5, paidOptionsPromoSchema as a6, userProfileReverseSchema as a7, clientPatch as a8, userProfileInfo as a9, adOwnerVerificationSchema as aA, users as aB, usersSchema as aC, changeEmailIsEligible as aD, changeEmailIsEligibleSchema as aE, userSubscriptions as aF, userSubscriptionsSchema as aG, wallet as aH, walletTypeSchema as aI, fullPayoutUser as aJ, fullPayoutUserSchema as aK, hyperwalletAuthToken as aL, tokenSchema as aM, payment as aN, paymentSchema as aO, serverPost as aP, purchasedPoOrder as aQ, purchasedPoOrderSchema as aR, plutusPurchaseToken as aS, plutusPurchaseTokenSchema as aT, shopGoogleReviews as aU, shopGoogleReviewsSchema as aV, extractSbtHeaders as aW, extractSbtAppHeaders as aX, requestIDHeaderName as aY, userProfileInfoSchema as aa, userProfile as ab, userProfileSchema as ac, poCartRedirect as ad, serverGet as ae, shippingRatesSchema as af, promotions as ag, promotionsSchema as ah, singleAdSchema as ai, loggedUser as aj, loggedUserSchema as ak, transaction as al, transactionSchema as am, transactions as an, transactionsSchema as ao, userTransaction as ap, userTransactionSchema as aq, transactionP2PStates as ar, transactionP2PStatesSchema as as, shop as at, shopSchema as au, poFees as av, feesSchema as aw, poFee as ax, poFeeSchema as ay, serverPut as az, voBannersSchema as b, clientGet as c, voucherValidatedOrder as d, voucherValidatedOrderSchema as e, clientPut as f, gallerizedSearchSchema as g, searchRecommendedSchema as h, filterValuesSchema as i, filterConfigSchema as j, userEmails as k, userEmailsHistorySchema as l, prices as m, pricesSchema as n, createdTransactionWithFlowURL as o, processBody as p, createdTransactionWithFlowUrlSchema as q, returnStatus as r, searchSchema as s, userContactInfo as t, userAdsSchema as u, voBanners as v, userContactInfoSchema as w, regionsSchema as x, geoEntriesSchema as y, zonesSchema as z };
//# sourceMappingURL=zod-BK1RGncv.js.map
